import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography
} from '@mui/material';
import {
  DocumentData,
  DocumentSnapshot,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import { Organization, Voter } from '../../types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SearchParams, removeIndexesOf, searchIndex, splitKeywords } from '../../utils/indexer';

import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

interface OrganizationListControlProps {
  onEdit?: (organization: Organization) => void;
  onDelete?: (organization: Organization) => void;
  onSelect?: (organization: Organization) => void;
}

const OrganizationListControl: React.FC<OrganizationListControlProps> = ({ onEdit, onDelete, onSelect }) => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationMembers, setOrganizationMembers] = useState<{[orgId: string]: Voter[]}>({});
  const [organizationMembersLoading, setOrganizationMembersLoading] = useState<{[orgId: string]: boolean}>({});
  const [isSearching, setIsSearching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadNext, setLoadNext] = useState(false);
  const [lastOrgRef, setLastOrgRef] = useState<DocumentSnapshot<DocumentData, DocumentData> | null>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<{[orgId: string]: boolean}>({});
  
  const PAGE_SIZE = 100;
  const searchInputRef = useRef<HTMLInputElement>(null);

  type OrganizationsPage = {
    organizations: Organization[];
    hasMore: boolean;
    lastSnapshotDoc: DocumentSnapshot<DocumentData, DocumentData> | null;
  };

  const fetchOrganizationMembers = async (organization: Organization) => {
    try {
      // Set loading state for this specific organization
      setOrganizationMembersLoading(prev => ({
        ...prev,
        [organization.id]: true
      }));

      const votersCollection = collection(db, 'voters');
      const votersQuery = query(votersCollection, where('organizations', 'array-contains', organization.name));
      const querySnapshot = await getDocs(votersQuery);
      
      const members = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      } as Voter));

      // Sort members by barangay
      const sortedMembers = members.sort((a, b) => 
        a.barangay.localeCompare(b.barangay) || a.name.localeCompare(b.name)
      );

      setOrganizationMembers(prev => ({
        ...prev,
        [organization.id]: sortedMembers
      }));

      // Clear loading state for this organization
      setOrganizationMembersLoading(prev => ({
        ...prev,
        [organization.id]: false
      }));
    } catch (error) {
      console.error('Error fetching organization members:', error);
      
      // Ensure loading state is cleared even if there's an error
      setOrganizationMembersLoading(prev => ({
        ...prev,
        [organization.id]: false
      }));
    }
  };

  const toggleOrganizationMembers = (organization: Organization) => {
    const isCurrentlyExpanded = expandedOrgs[organization.id];
    
    setExpandedOrgs(prev => ({
      ...prev,
      [organization.id]: !isCurrentlyExpanded
    }));

    if (!isCurrentlyExpanded) {
      fetchOrganizationMembers(organization);
    }
  };

  const searchOrganizations = async (
    keyword: string,
    searchAfter: DocumentSnapshot<DocumentData, DocumentData> | null
  ): Promise<OrganizationsPage> => {
    const newOrganizations: Organization[] = [];
    const searchParams: SearchParams = {
      keywords: splitKeywords(keyword),
      resultLimit: PAGE_SIZE,
      document: "organization",
      field: "name",
      searchAfter: searchAfter
    }
    const result = await searchIndex(searchParams);    
    for (const index of result.indexes) {
      const [collectionName, id] = index.documentPath.split('/');
      const orgRef = doc(db, collectionName, id);
      const orgSnap = await getDoc(orgRef);
      
      if (orgSnap.exists()) {
        const orgData = orgSnap.data() as Organization;
        newOrganizations.push({
          ...orgData,
          id: orgSnap.id
        });
      }
    }

    return {
      organizations: newOrganizations,
      hasMore: result.indexes.length >= searchParams.resultLimit,
      lastSnapshotDoc: result.lastSnapshotDoc
    };
  };

  const unsearchedList = async (
    searchAfter: DocumentSnapshot<DocumentData, DocumentData> | null
  ): Promise<OrganizationsPage> => {
    if (!hasMore && searchAfter) {
      return {
        organizations: [],
        hasMore: false,
        lastSnapshotDoc: null
      };
    }

    const orgsCollection = collection(db, 'organizations');
    let orgsQuery = query(orgsCollection, orderBy('name'), limit(PAGE_SIZE));

    if (searchAfter && organizations.length > 0) {
      orgsQuery = query(orgsQuery, startAfter(searchAfter));
    }

    const querySnapshot = await getDocs(orgsQuery);
    const newOrgs = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Organization[];

    return {
      organizations: newOrgs,
      hasMore: newOrgs.length >= PAGE_SIZE,
      lastSnapshotDoc: newOrgs.length > 0 ? querySnapshot.docs[querySnapshot.docs.length - 1] : searchAfter
    };
  };

  const fetchOrganizations = async (
    keyword: string,
    searchAfter: DocumentSnapshot<DocumentData, DocumentData> | null
  ): Promise<OrganizationsPage> => {
    try {
      return keyword ? await searchOrganizations(keyword, searchAfter) : await unsearchedList(searchAfter);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
    return {
      organizations: [],
      hasMore: false,
      lastSnapshotDoc: null
    };
  };

  useEffect(() => {
    setLoading(true);
    fetchOrganizations('', null).then((result) => {
      setOrganizations(result.organizations); 
      setHasMore(result.hasMore);
      setLastOrgRef(result.lastSnapshotDoc);
      setLoading(false);
    });
  }, []);
  
  const performSearch = () => {
    setLoading(true);
    setIsSearching(true);
    const keyword = searchInputRef.current?.value ?? '';
    fetchOrganizations(keyword, null).then((result) => {
      setOrganizations(result.organizations); 
      setHasMore(result.hasMore);
      setLastOrgRef(result.lastSnapshotDoc);
      setLoading(false);
      setIsSearching(false);
    });
  };

  useEffect(() => {
    if (loadNext) {
      setLoading(true);
      const searchKeyword = searchInputRef.current?.value ?? '';
      fetchOrganizations(searchKeyword, lastOrgRef).then((result) => {
        setOrganizations(prevOrgs => [...prevOrgs, ...result.organizations]); 
        setHasMore(result.hasMore);
        setLastOrgRef(result.lastSnapshotDoc);
        setLoading(false);
      });
      setLoadNext(false);
    }
  }, [loadNext]);
  
  const observer = useRef<IntersectionObserver | null>(null);
  
  const lastOrgElementRef = useCallback((node: HTMLDivElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) {
        setLoadNext(true);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handleEdit = (organization: Organization) => {
    if (onEdit) {
      onEdit(organization);
    }
  };

  const handleDelete = async (organization: Organization) => {
    try {
      await deleteDoc(doc(db, 'organizations', organization.id));
      await removeIndexesOf("organization", "name", `organizations/${organization.id}`);
      
      setOrganizations((prevOrgs) => prevOrgs.filter((org) => org.id !== organization.id));
      if (onDelete) {
        onDelete(organization);
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
    }
  };

  const renderOrganizationMembers = (members: Voter[]) => {
    // Group members by barangay
    const membersByBarangay = members.reduce((acc, member) => {
      if (!acc[member.barangay]) {
        acc[member.barangay] = [];
      }
      acc[member.barangay].push(member);
      return acc;
    }, {} as {[barangay: string]: Voter[]});

    return (
      <Grid container spacing={2}>
        {Object.entries(membersByBarangay).map(([barangay, barangayMembers]) => (
          <Grid item xs={12} key={barangay}>
            <Typography variant="subtitle1" gutterBottom>
              {barangay}
            </Typography>
            <Grid container spacing={1}>
              {barangayMembers.map((member, memberIndex) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={member.id}>
                  <ListItem>
                    <ListItemText 
                      primary={`${memberIndex + 1}. ${member.name}`} 
                      secondary={`PRECINCT ${member.precinct}`} 
                    />
                  </ListItem>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <TextField
          fullWidth
          label="Keyword"
          variant="outlined"
          inputRef={searchInputRef}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              performSearch();
            }
          }}
        />
        &nbsp;
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={performSearch}
          disabled={isSearching}
        >
          Search
        </Button>
      </Box>
      {isSearching && <Box mb={2}><LinearProgress color="primary" /></Box>}
      {organizations.map((org, index) => (
        <Card 
          key={org.id} 
          sx={{ mb: 2 }}
          ref={index === organizations.length - 1 ? lastOrgElementRef : null}
        >
          <CardContent>
            <Typography variant="h6">{org.name}</Typography>
            <Typography variant="body2">{org.description}</Typography>
            <Typography variant="body2">Contact: {org.contact}</Typography>
            <Typography variant="body2">Email: {org.email}</Typography>
            <Button 
              size="small" 
              color="primary" 
              onClick={() => toggleOrganizationMembers(org)}
            >
              {expandedOrgs[org.id] ? 'Hide Members' : 'Show Members'}
            </Button>
            <Collapse in={expandedOrgs[org.id]}>
              {organizationMembersLoading[org.id] ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                  <CircularProgress size={40} />
                </Box>
              ) : organizationMembers[org.id] && organizationMembers[org.id].length > 0 ? (
                renderOrganizationMembers(organizationMembers[org.id])
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No members found
                </Typography>
              )}
            </Collapse>
          </CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            {onEdit && user?.permissions?.manage_organizations && (
              <Button 
                size="small" 
                color="primary" 
                onClick={() => handleEdit(org)} 
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
            )}
            {onDelete && user?.permissions?.admin && (
              <Button 
                size="small" 
                color="error" 
                onClick={() => handleDelete(org)} 
              >
                Delete
              </Button>
            )}
            {onSelect && (
              <Button size="small" color="primary" onClick={() => onSelect(org)}>
                Select
              </Button>
            )}
          </Box>
        </Card>
      ))}
      {loading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
      {!loading && organizations.length === 0 && <Typography align="center">No Organizations available</Typography>}
      {!loading && !hasMore && organizations.length > 0 && <Typography align="center">No more organizations to load. Total organizations: {organizations.length}</Typography>}
    </>
  );
};

export default OrganizationListControl;
